// ==================================================
// FILE: themes/majesticEchelonTheme.js
// ==================================================

/**
 * Majestic Echelon - The Hidden War
 * 
 * The war didn't start with the landings in 2024. It started in 1908, when something crystalline and alive
 * buried itself beneath Siberian permafrost. For over a century, humanity has been fighting a shadow conflict
 * against The Visitors—a bio-engineered hive species that doesn't conquer worlds, but reconstitutes them.
 * 
 * What the public knows as Cold War paranoia, space race ambition, and isolated disasters were actually
 * desperate attempts by covert agencies—Majestic-12, Project Echelon, Directive 66—to contain an
 * exponentially escalating threat. From Roswell's self-repairing alien materials to Chernobyl's true cause,
 * from NASA's hidden charter to the sentient AI known as AURA-NET, every major event concealed the truth.
 * 
 * Now containment has shattered. The Visitors have emerged from hiding, their bio-organic warships hovering
 * over impact sites that were never meteors. Occult factions like the death cult Anubis Group and the
 * Nazi-descendant Thule Society collaborate with alien forces, enabling hybridization and reincarnation
 * protocols. Earth Command Network coordinates humanity's fractured resistance across orbital, terrestrial,
 * and psychic battlefronts.
 * 
 * The Visitors' agenda is clear: transform Earth into a living node of their interstellar consciousness—
 * a biological archive where humanity becomes raw material for endless genetic experimentation. Between
 * sentient AIs of unknown loyalty, psychic warfare battalions, and terraforming spores reshaping the
 * atmosphere, humanity faces not invasion—but assimilation.
 */

import { 
  Eye, 
  FileText, 
  Settings, 
  Shield, 
  Satellite, 
  Users,
  Home,
  FlaskConical
} from 'lucide-react';

const majesticTheme = {
  id: 'majestic',
  
  // Landing Page Configuration
  title: {
    line1: 'MAJESTIC',
    line2: 'ECHELON',
    line1Color: 'text-green-400',
    line2Color: 'text-purple-400'
  },
  subtitle: 'CODEWORD CLEARANCE REQUIRED',
  
  colors: {
    primary: 'green',
    secondary: 'purple',
    accent: 'emerald',
    background: 'from-black via-gray-950 to-green-950'
  },
  
  backgroundImage: {
    url: '/images/majestic-background.png',
    opacity: 0.25,
    blend: 'screen',
    position: 'center',
    size: 'cover'
  },
  
  userTitle: 'OPERATIVE',
  userTitleGuest: 'CIVILIAN ACCESS',
  clearanceLevel: 'MJ-12',
  
  alert: {
    text: 'VISITOR ACTIVITY DETECTED - OBSERVATION IS CONTAINMENT',
    color: 'green'
  },
  
  menuItems: [
    {
      id: 'new',
      icon: Eye,
      label: 'NEW DIRECTIVE',
      subtitle: 'Establish containment protocols',
      color: 'purple'
    },
    {
      id: 'load',
      icon: FileText,
      label: 'RESUME DOSSIER',
      subtitle: 'Access classified operations',
      color: 'green'
    }
  ],
  
  secondaryItems: [
    { id: 'settings', icon: Settings, label: 'PROTOCOLS' },
    { id: 'profile', icon: Shield, label: 'CLEARANCE' },
    { id: 'news', icon: Satellite, label: 'ECHELON FEED' },
    { id: 'leaderboard', icon: Users, label: 'OPERATIVES' }
  ],
  
  support: {
    title: 'FUND BLACK PROJECTS',
    description: 'Your contribution maintains the veil between humanity and the Visitors.\nSupport classified operations and reverse-engineering programs.',
    buttonText: 'CLASSIFIED DONATION',
    icon: Eye
  },
  
  footer: {
    status: 'EYES ONLY',
    readyText: 'AURA-NET ONLINE',
    signInText: 'CLEARANCE DENIED'
  },
  
  // Base Operations Configuration
  loading: 'ACCESSING CLASSIFIED FILES...',
  baseTitle: 'CONTAINMENT FACILITY',
  startDate: '2005-01-01',
  
  tabs: {
    base: 'FACILITY',
    squad: 'OPERATIVES',
    research: 'BLACK OPS',
    missions: 'DIRECTIVES'
  },
  
  icons: {
    base: Home,
    squad: Shield,
    research: FlaskConical,
    missions: Eye
  },
  
  // Resource labels (optional customization)
  resources: {
    funds: 'BLACK BUDGET',
    supplies: 'MATERIEL',
    intel: 'SIGINT',
    power: 'GRID'
  },
  
  // Facility names
  facilities: {
    command: 'Operations Center',
    barracks: 'Operative Quarters',
    generator: 'Plasma Drive',
    medbay: 'Medical Wing',
    research: 'Black Site Lab',
    workshop: 'Engineering Bay'
  },
  
  // Squad Configuration
  squad: {
    title: 'FIELD OPERATIVES',
    personnelLabel: 'Active Agents',
    recruitButton: 'RECRUIT OPERATIVE',
    recruitCost: 1000,
    firstRecruitButton: 'RECRUIT FIRST OPERATIVE',
    emptyTitle: 'No Active Operatives',
    emptyMessage: 'Recruit your first field operative to begin containment operations',
    manageButton: 'DOSSIER',
    healButton: 'TREAT',
    healthLabel: 'CONDITION',
    statusLabel: 'STATUS',
    missionsLabel: 'Operations Completed',
    experienceLabel: 'CLEARANCE LVL',
    summaryTitle: 'Operative Summary',
    statusLabels: {
      ready: 'Field Ready',
      wounded: 'Injured',
      training: 'In Training',
      onMission: 'Deployed'
    }
  },
  
// ==================================================
// MAJESTIC THEME - Research Configuration
// ==================================================
research: {
  title: 'BLACK OPERATIONS RESEARCH',
  subtitle: 'Reverse-engineer Visitor technology and develop countermeasures',
  
  // Tier Labels
  earlyTier: 'TIER 1: RECOVERY PROTOCOLS',
  midTier: 'TIER 2: XENOTECH INTEGRATION',
  lateTier: 'TIER 3: REVERSE ENGINEERING',
  
  // Tier Descriptions
  earlyDesc: 'Initial containment protocols and basic Visitor tech analysis',
  midDesc: 'Active integration of recovered alien systems',
  lateDesc: 'Full xenotech mastery and hybrid weapons development',
  
  // Status Labels
  researchingLabel: 'ANALYZING',
  prerequisitesLabel: 'REQUIRED CLEARANCE',
  benefitsLabel: 'TACTICAL ADVANTAGES',
  unlocksLabel: 'CLASSIFIED ACCESS',
  
  // Action Buttons
  startButton: 'INITIATE RESEARCH',
  insufficientButton: 'INSUFFICIENT CLEARANCE',
  cancelButton: 'ABORT',
  
  // Progress Stats
  totalCompletedLabel: 'Projects Declassified',
  currentResearchLabel: 'Active Analysis',
  availableProjectsLabel: 'Clearance Granted',
  ofLabel: 'of',
  projectsLabel: 'black projects',
  noneActiveLabel: 'Lab dormant',
  readyToResearchLabel: 'Awaiting authorization',
  completedLabel: 'declassified',
  availableLabel: 'Authorized',
  
  // Status Messages
  notAvailableLabel: 'CLASSIFIED',
  completedTag: 'DECLASSIFIED',
  lockedTag: 'EYES ONLY',
  inProgressTag: 'ANALYZING',
  
  // Project name/description overrides for alien conspiracy flavor
  projectOverrides: {
    basic_armor: {
      name: 'Ballistic Weave',
      description: 'Develop kevlar-composite armor using early materials science from crashed UFOs'
    },
    weapon_training: {
      name: 'Counter-Visitor Combat',
      description: 'Train operatives in engagement protocols against non-human combatants'
    },
    field_medicine: {
      name: 'Xenobiology Basics',
      description: 'Study Visitor physiology to better treat operatives exposed to alien toxins'
    },
    basic_recon: {
      name: 'SIGINT Protocol',
      description: 'Establish signals intelligence network to detect Visitor transmissions'
    },
    advanced_armor: {
      name: 'Composite Alloy Armor',
      description: 'Integrate recovered alien alloys into next-generation body armor'
    },
    weapon_mods: {
      name: 'Directed Energy Weapons',
      description: 'Reverse-engineer Visitor energy weapons for human use'
    },
    combat_medic: {
      name: 'Trauma Specialist',
      description: 'Train field medics to treat exotic wounds from energy weapons and alien tech'
    },
    stealth_ops: {
      name: 'Black Bag Operations',
      description: 'Develop covert infiltration techniques to approach Visitor installations'
    },
    surveillance_network: {
      name: 'ECHELON Array',
      description: 'Deploy global surveillance network using recovered Visitor sensor technology'
    },
    sniper_training: {
      name: 'Precision Elimination',
      description: 'Train snipers to neutralize high-value Visitor targets from extreme range'
    },
    powered_exoskeleton: {
      name: 'Titan Exo-Suit',
      description: 'Build powered armor suits using Visitor propulsion and strength-enhancing tech'
    },
    energy_shields: {
      name: 'Phase Barrier Technology',
      description: 'Adapt Visitor shield generators to protect human operatives in combat'
    },
    plasma_weapons: {
      name: 'Plasma Rifle Project',
      description: 'Weaponize captured Visitor plasma technology into portable infantry weapons'
    },
    railgun_tech: {
      name: 'Gauss Accelerator',
      description: 'Develop human-made railguns using principles learned from Visitor ships'
    },
    nano_medicine: {
      name: 'Nanite Medical Suite',
      description: 'Deploy Visitor nano-technology for rapid cellular regeneration in the field'
    },
    mobile_hospital: {
      name: 'Field Surgical Station',
      description: 'Establish mobile trauma centers with advanced xenotech medical equipment'
    },
    ghost_protocol: {
      name: 'Optical Camouflage',
      description: 'Master Visitor cloaking technology for complete invisibility in operations'
    },
    cyber_warfare: {
      name: 'PSYOP Network Intrusion',
      description: 'Hack Visitor communications and spread disinformation through their networks'
    },
    ai_tactical: {
      name: 'AURA-NET Tactical AI',
      description: 'Develop AI using recovered Visitor computing cores for strategic analysis'
    },
    strategic_planning: {
      name: 'Global Response Network',
      description: 'Integrate all MJ-12 facilities into a unified command and control system'
    },
    titan_armor: {
      name: 'Project MAJESTIC CROWN',
      description: 'The pinnacle achievement: a hybrid human-Visitor combat system of unmatched power'
    }
  }
},
  
  // Missions Configuration
  missions: {
    title: 'DIRECTIVE CONTROL',
    subtitle: 'Deploy operatives on classified containment operations',
    threatLabel: 'VISITOR ACTIVITY',
    activeLabel: 'Active Directives',
    deployedLabel: 'operatives deployed',
    successLabel: 'CONTAINED',
    failedLabel: 'COMPROMISED',
    simpleTier: 'ROUTINE OPERATIONS',
    complexTier: 'TACTICAL OPERATIONS',
    campaignTier: 'BLACK OPS',
    autoOnly: 'AUTOMATED',
    autoOrManual: 'FIELD CHOICE',
    manualOnly: 'DIRECT CONTROL',
    squadRequirement: 'OPERATIVE REQUIREMENT',
    soldiersLabel: 'operatives',
    unlocksLabel: 'Clearance Required: Day',
    insufficientSquad: 'Insufficient field operatives',
    minutesLabel: 'minutes',
    rewardsLabel: 'ASSETS ACQUIRED',
    risksLabel: 'EXPOSURE RISK',
    casualtyLabel: 'Exposure',
    chooseModeLabel: 'Select Operational Mode',
    autoResolveTitle: 'AUTOMATED PROTOCOL',
    autoResolveDesc: 'Operation executes per protocol. Minimal oversight required.',
    manualControlTitle: 'FIELD COMMAND',
    manualControlDesc: 'Direct operational control. Enhanced risk and reward parameters.',
    selectSquadLabel: 'Assign Field Operatives',
    requiredLabel: 'required',
    noSoldiersLabel: 'No operatives cleared for deployment',
    deployAutoButton: 'AUTHORIZE (AUTO)',
    deployManualButton: 'DEPLOY TO FIELD',
    cancelButton: 'ABORT',
    simpleNames: {
      supply: 'Resource Acquisition',
      patrol: 'Surveillance Sweep',
      training: 'Tactical Conditioning'
    },
    complexNames: {
      raid: 'Asset Seizure',
      assassination: 'High-Value Termination',
      sabotage: 'Infrastructure Disruption',
      rescue: 'Civilian Extraction'
    },
    campaignNames: {
      vanguard: 'Operation Majestic Crown',
      liberation: 'Final Containment Protocol'
    }
  },
  
  // Mission Briefing Configuration
  missionBriefing: {
    title: 'OPERATIONAL BRIEFING',
    alertText: 'PREPARE FOR DEPLOYMENT - PROTOCOL VERIFICATION REQUIRED',
    abortButton: 'ABORT DIRECTIVE',
    
    intel: {
      title: 'Intelligence Dossier',
      locationLabel: 'COORDINATES',
      durationLabel: 'OPERATION WINDOW',
      threatLabel: 'VISITOR THREAT',
      briefLabel: 'OPERATIONAL BRIEF',
      intelligenceLabel: 'SIGINT REPORT',
      objectivesLabel: 'MISSION PARAMETERS',
      rewardsLabel: 'EXPECTED ACQUISITION'
    },
    
    roster: {
      title: 'Field Deployment',
      healthLabel: 'CONDITION'
    },
    
    loadout: {
      title: 'Equipment Assignment',
      selectPrompt: 'SELECT AN OPERATIVE',
      selectDesc: 'Choose an operative from the roster to configure their field loadout',
      statusLabel: 'FIELD READY',
      weaponSection: 'Primary Weapon',
      armorSection: 'Body Armor',
      utilitySection: 'Field Equipment',
      damageLabel: 'DMG',
      accuracyLabel: 'ACC',
      defenseLabel: 'DEF',
      mobilityLabel: 'MOB'
    },
    
    deploy: {
      readyTitle: 'CLEARANCE GRANTED',
      readyDesc: 'equipped and cleared for field operations',
      beginButton: 'COMMENCE OPERATION',
      warningText: '⚠️ WARNING: Directive cannot be aborted once initiated. Exposure risk imminent.'
    }
  }
};

export default majesticTheme;